import { useSettingsStore } from '@st/core/stores/useSettingsStore'
import { useBonusesCountStore } from '../../stores/useBonusesCountStore'
import { useRegularBonusesStore } from '../../stores/useRegularBonusesStore'
import type { ExtendedBonus, BonusType, BaseBonus } from './types'
import rakebackImg from '../../assets/rakeback.png'
import cashbackImg from '../../assets/cashback.png'
import reloadImg from '../../assets/reload.png'
import freespinImg from '../../assets/freespins.png'
import freebetImg from '../../assets/freebet.png'
import tournamentImg from '../../assets/tournament.png'
import referalImg from '../../assets/referal.png'
import promoImg from '../../assets/promo.png'
import depositImg from '../../assets/deposit.png'
import { useBonusesDeps } from '../../useDeps'

export function useAllBonusesGrid() {
  const { t } = useI18n()
  const { settings } = storeToRefs(useSettingsStore())
  const { isAuthenticated } = useBonusesDeps()
  const { freebetsCount, freespinsCount, depositBonusesCount } = storeToRefs(
    useBonusesCountStore(),
  )
  const {
    bonuses: regularBonuses,
    cashbackPrograms,
    reloadPrograms,
  } = storeToRefs(useRegularBonusesStore())

  const isRakeBackExist = computed(() =>
    regularBonuses.value.find((bonus) => bonus.bonusProgramType === 'rakeBack'),
  )
  const isReloadExist = computed(() =>
    regularBonuses.value.find((bonus) => bonus.bonusProgramType === 'reload'),
  )
  const isCashbackExist = computed(() =>
    regularBonuses.value.find((bonus) => bonus.bonusProgramType === 'cashback'),
  )
  const isRatRaceExist = computed(() =>
    regularBonuses.value.find((bonus) => bonus.bonusProgramType === 'ratRace'),
  )
  const cashbackProgramsCount = computed(() => cashbackPrograms.value?.length)
  const reloadProgramsCount = computed(() => reloadPrograms.value?.length)

  const bonusMap: ComputedRef<Record<BonusType, BaseBonus>> = computed(() => ({
    depositBonus: {
      icon: depositImg,
      title: t('bonuses.depostBonusTitle'),
      text: t('bonuses.depostBonusText'),
      background: 'red',
      checked: !!depositBonusesCount.value && isAuthenticated.value,
    },
    rakeBack: {
      icon: rakebackImg,
      title: t('bonuses.rakeBackTitle'),
      text: t('bonuses.rakeBackText'),
      background: 'blue',
      checked: !!isRakeBackExist.value && isAuthenticated.value,
    },
    cashback: {
      icon: cashbackImg,
      title: t('bonuses.cashBackTitle'),
      text: t('bonuses.cashBackText'),
      background: 'blue',
      checked:
        (!!isCashbackExist.value || !!cashbackProgramsCount.value) &&
        isAuthenticated.value,
    },
    reload: {
      icon: reloadImg,
      title: t('bonuses.reloadTitle'),
      text: t('bonuses.reloadText'),
      background: 'blue',
      checked:
        (!!isReloadExist.value || !!reloadProgramsCount.value) &&
        isAuthenticated.value,
    },
    freespins: {
      icon: freespinImg,
      title: t('bonuses.freespinTitle'),
      text: t('bonuses.freespinText'),
      background: 'green',
      checked: !!freespinsCount.value && isAuthenticated.value,
    },
    freebet: {
      icon: freebetImg,
      title: t('bonuses.freeBetTitle'),
      text: t('bonuses.freeBetText'),
      background: 'green',
      checked: !!freebetsCount.value && isAuthenticated.value,
    },
    tournaments: {
      icon: tournamentImg,
      title: t('bonuses.tournamentTitle'),
      text: t('bonuses.tournamentText'),
      background: 'bronze',
      link: '/tournaments',
      checked: !!isRatRaceExist.value && isAuthenticated.value,
    },
    referralProgram: {
      icon: referalImg,
      title: t('bonuses.referalProgramTitle'),
      text: t('bonuses.referalProgramText'),
      background: 'bronze',
      link: isAuthenticated.value ? '/refer-and-earn' : '/refer-info',
    },
    promocodes: {
      icon: promoImg,
      title: t('bonuses.promoCodeTitle'),
      text: t('bonuses.promoCodeText'),
      background: 'bronze',
      link: 'https://t.me/BetoneComBot',
    },
  }))

  const bonuses = computed(
    () => settings.value?.frontBonusProgramsForUnauthorized,
  )

  const extentedBonuses = computed<ExtendedBonus[]>(
    () =>
      bonuses.value
        ?.filter((bonus) => bonus.isEnabled)
        .sort((a, b) => (a.weight > b.weight ? -1 : 1))
        .map((bonus) => ({
          ...bonus,
          ...bonusMap.value[bonus.type as BonusType],
        })) ?? [],
  )

  return {
    extentedBonuses,
  }
}
